import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Phone, Mail, Globe, Star, ExternalLink, BadgeCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../components/ui/dialog';
import Flag from '../components/world/Flag';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';
import {
  adresaOdbornika, mestoOdbornika, uliceOdbornika, regionOdbornika,
  odkazDoMap, mapaOdbornika, stitekKategorie,
} from '../lib/odbornik';

/**
 * Detail odborníka v Bloom Worldu.
 *
 * Odborníci leží ve vlastní kolekci, ne v katalogu, takže je nezobrazí
 * `DirectoryDetailPage`. Do téhle chvíle měla Bloom World jen výpis a všechno
 * podstatné se muselo vejít na kartu; recenze se nikam nevešly, i když je
 * server umí od začátku.
 *
 * Ukazuje totéž co detail u českých odborníků — mapa, kontakty, hodnocení
 * a všechny recenze. Recenze píšou lidi, takže se nepřekládají.
 */
export default function CountrySpecialistDetailPage() {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, datum, dvojjazycne } = useJazyk();

  const [odbornik, setOdbornik] = useState(null);
  const [kategorie, setKategorie] = useState([]);
  const [zeme, setZeme] = useState(null);
  const [recenze, setRecenze] = useState([]);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');

  const [otevrenoHodnoceni, setOtevrenoHodnoceni] = useState(false);
  const [hvezdy, setHvezdy] = useState(0);
  const [text, setText] = useState('');
  const [odesila, setOdesila] = useState(false);

  useEffect(() => {
    axios.get(`${API}/specialist-categories`)
      .then((r) => setKategorie(r.data || []))
      .catch(() => { /* bez kategorií se ukáže celý název oboru */ });
  }, []);

  const nactiRecenze = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/specialists/${id}/reviews`);
      setRecenze(data || []);
    } catch { /* detail dává smysl i bez recenzí */ }
  }, [id]);

  useEffect(() => {
    let zivy = true;
    setNacita(true);
    setChyba('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/specialists/${id}`);
        if (!zivy) return;
        setOdbornik(data);
        await nactiRecenze();
        // Vlaječka a jméno země jsou ozdoba, ne obsah – když se nenačtou,
        // detail se kvůli tomu neshodí.
        try {
          const z = await axios.get(`${API}/geo/countries/${slug}`);
          if (zivy) setZeme(z.data);
        } catch { /* nevadí */ }
      } catch (e) {
        if (zivy) setChyba(e?.response?.status === 404 ? 'nenalezen' : 'chyba');
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, [id, slug, nactiRecenze]);

  const odeslatRecenzi = async (e) => {
    e.preventDefault();
    if (hvezdy === 0 || odesila) return;
    setOdesila(true);
    try {
      await axios.post(`${API}/specialists/${id}/reviews`,
                       { specialist_id: id, rating: hvezdy, content: text });
      toast.success(t('odb.recenzePridana'));
      setOtevrenoHodnoceni(false);
      setHvezdy(0);
      setText('');
      // Průměr i počet se počítají na serveru, tak si detail vyzvedneme znovu.
      try {
        const { data } = await axios.get(`${API}/specialists/${id}`);
        setOdbornik(data);
      } catch { /* hvězdičky se srovnají po obnovení stránky */ }
      nactiRecenze();
    } catch {
      toast.error(t('odb.chybaRecenze'));
    } finally {
      setOdesila(false);
    }
  };

  if (nacita) {
    return <div className="py-24 flex justify-center"><div className="spinner" /></div>;
  }

  if (chyba || !odbornik) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">
          {chyba === 'nenalezen' ? t('detail.nenalezen') : t('vypis.chyba')}
        </p>
        <button type="button" onClick={() => navigate(`/world/${slug}/specialists`)}
                className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('sekce.specialists')}
        </button>
      </div>
    );
  }

  const nazevZeme = dvojjazycne(zeme?.name_cs, zeme?.name_en);
  const mapa = mapaOdbornika(odbornik);
  const mesto = mestoOdbornika(odbornik);
  const ulice = uliceOdbornika(odbornik);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="odbornik-detail">
      <button type="button" onClick={() => navigate(`/world/${slug}/specialists`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" />
        {nazevZeme ? t('detail.zpetNa', { zeme: nazevZeme }) : t('sekce.specialists')}
      </button>

      <header className="mt-4">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text">
          {odbornik.name}
        </h1>
        {odbornik.specialty && (
          <p className="text-bloom-violet font-medium mt-0.5">
            {stitekKategorie(odbornik.specialty, kategorie)}
          </p>
        )}
        {(mesto || zeme) && (
          <p className="mt-1.5 text-sm text-bloom-sub flex items-center gap-1.5">
            {zeme && <Flag emoji={zeme.flag} iso2={zeme.iso2} size="sm" />}
            {[mesto, nazevZeme].filter(Boolean).join(', ')}
          </p>
        )}
        <Hodnoceni odbornik={odbornik} />
      </header>

      {odbornik.description && (
        <p className="mt-4 text-bloom-sub leading-relaxed">{odbornik.description}</p>
      )}

      {mapa && (
        <div className="mt-5 rounded-2xl overflow-hidden border border-border/40">
          {/* `loading="lazy"`, ať se mapa netahá dřív, než na ni někdo dojede. */}
          <iframe src={mapa} title={odbornik.name} loading="lazy"
                  className="w-full aspect-video" style={{ border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade" />
        </div>
      )}

      <section className="mt-5 bg-white border border-border/40 rounded-2xl p-4 sm:p-5">
        <h2 className="font-heading font-bold text-bloom-text">{t('detail.kontakty')}</h2>
        <dl className="mt-3 space-y-2.5 text-sm">
          {(ulice || mesto) && (
            <Radek ikona={MapPin}>
              <span className="text-bloom-text">{adresaOdbornika(odbornik)}</span>
              {regionOdbornika(odbornik) && (
                <span className="text-bloom-sub">, {regionOdbornika(odbornik)}</span>
              )}
            </Radek>
          )}
          {odbornik.phone && (
            <Radek ikona={Phone}>
              <a href={`tel:${odbornik.phone}`} className="text-bloom-text hover:text-bloom-violet">
                {odbornik.phone}
              </a>
            </Radek>
          )}
          {odbornik.email && (
            <Radek ikona={Mail}>
              <a href={`mailto:${odbornik.email}`} className="text-bloom-text hover:text-bloom-violet">
                {odbornik.email}
              </a>
            </Radek>
          )}
          {odbornik.website && (
            <Radek ikona={Globe}>
              <a href={odbornik.website} target="_blank" rel="noopener noreferrer"
                 className="text-bloom-text hover:text-bloom-violet inline-flex items-center gap-1">
                {t('odb.web')}<ExternalLink className="w-3 h-3" />
              </a>
            </Radek>
          )}
        </dl>

        {odbornik.assigned_locations?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/40">
            <p className="text-xs font-semibold text-bloom-sub">{t('odb.dostupnyV')}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {odbornik.assigned_locations.map((m) => (
                <span key={m} className="px-2.5 py-1 rounded-lg bg-bloom-violet/10 text-bloom-violet text-xs">
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <a href={odkazDoMap(odbornik)} target="_blank" rel="noopener noreferrer" className="flex-1">
            <Button variant="outline" className="w-full text-bloom-violet border-bloom-violet/30">
              <MapPin className="w-4 h-4 mr-1" />{t('odb.otevritVMapach')}
            </Button>
          </a>
          {/* Bez ověřeného e-mailu tlačítko není, stejně jako u českých odborníků. */}
          {user?.email_verified && (
            <Dialog open={otevrenoHodnoceni} onOpenChange={setOtevrenoHodnoceni}>
              <DialogTrigger asChild>
                <Button className="flex-1 bg-bloom-violet text-white" data-testid="pridat-recenzi">
                  <Star className="w-4 h-4 mr-1" />{t('odb.pridatRecenzi')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-bloom-text">
                    {t('odb.pridatRecenzi')}
                  </DialogTitle>
                  <DialogDescription>{t('odb.ohodnotte')}</DialogDescription>
                </DialogHeader>
                <form onSubmit={odeslatRecenzi} className="space-y-4">
                  <div>
                    <Label className="text-bloom-text">{t('odb.vaseHodnoceni')}</Label>
                    <div className="flex gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setHvezdy(n)} className="p-0.5"
                                aria-label={String(n)} aria-pressed={n === hvezdy}>
                          <Star className={`w-7 h-7 ${n <= hvezdy
                            ? 'text-bloom-pride-pink fill-bloom-pride-pink' : 'text-border'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-bloom-text">{t('odb.popisZkusenosti')}</Label>
                    <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                              required placeholder={t('odb.popisteZkusenost')} />
                  </div>
                  <Button type="submit" className="w-full bg-bloom-violet text-white"
                          disabled={hvezdy === 0 || odesila}>
                    {odesila ? t('odb.odesilam') : t('odb.odeslatRecenzi')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="font-heading text-lg font-semibold text-bloom-text">
          {t('odb.recenze', { pocet: recenze.length })}
        </h2>
        {recenze.length === 0 ? (
          <p className="text-bloom-sub text-sm text-center py-8">{t('odb.zadneRecenze')}</p>
        ) : (
          <ul className="mt-3 space-y-3" data-testid="recenze-seznam">
            {recenze.map((r) => (
              <li key={r.id} className="bg-white border border-border/40 rounded-2xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-bloom-text text-sm">{r.username}</span>
                  <span className="flex shrink-0"><Hvezdicky pocet={r.rating} /></span>
                </div>
                {r.content && <p className="mt-2 text-sm text-bloom-sub whitespace-pre-line">{r.content}</p>}
                <p className="mt-2 text-[11px] text-bloom-sub">{datum(r.created_at)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Řádek kontaktu s ikonou. */
function Radek({ ikona: Ikona, children }) {
  return (
    <div className="flex items-start gap-2">
      <Ikona className="w-4 h-4 text-bloom-violet mt-0.5 shrink-0" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

/** Pět hvězdiček, z toho `pocet` plných. */
function Hvezdicky({ pocet }) {
  return [...Array(5)].map((_, i) => (
    <Star key={i} className={`w-3.5 h-3.5 ${i < pocet
      ? 'text-bloom-pride-pink fill-bloom-pride-pink' : 'text-border'}`} aria-hidden="true" />
  ));
}

/** Průměr a počet recenzí. Bez jediné recenze se nekreslí prázdných pět hvězd. */
function Hodnoceni({ odbornik }) {
  const { t } = useJazyk();
  if (!odbornik.review_count) return null;

  return (
    <p className="mt-2 flex items-center gap-2 text-sm">
      <span className="flex"><Hvezdicky pocet={Math.round(odbornik.avg_rating)} /></span>
      <span className="font-semibold text-bloom-text">{odbornik.avg_rating.toFixed(1)}</span>
      <span className="text-bloom-sub">
        ({odbornik.review_count} {t('odb.recenzi', { pocet: odbornik.review_count })})
      </span>
      {odbornik.status === 'approved' && (
        <BadgeCheck className="w-4 h-4 text-bloom-violet" aria-hidden="true" />
      )}
    </p>
  );
}
